import { Link, useSearchParams } from "react-router-dom";
import {
  Company,
  companySource,
  Flag,
  Nugget,
  RootState,
  Tag,
} from "../../redux/utils/types";
import { EmptyList } from "../EmptyList";
import { useEffect } from "react";
import styles from "./style.module.css";
import Paginate from "../Paginate";
import { useDispatch, useSelector } from "react-redux";
import { getCompanyPage } from "../../redux/modules/company/actions";
import { escapeRegExp } from "lodash";

const Highlighted = (props: { text: string; highlights: string[]; phrase?: string }) => {
  const { text = "", highlights = [], phrase = "" } = props;
  if (!text || (highlights.length === 0 && !phrase.trim())) {
    return <span>{text}</span>;
  }

  const pattern = [
    phrase.trim() ? escapeRegExp(phrase.trim()) : "",
    ...highlights.map((term) => `\\b${escapeRegExp(term)}\\b`),
    ...highlights.map((term) => escapeRegExp(term)),
  ].filter(Boolean);
  const regex = new RegExp(`(${pattern.join("|")})`, "gi");
  const parts = text.split(regex);

  return (
    <span>
      {parts
        .filter((part) => part)
        .map((part, i) =>
          pattern.some((item) => new RegExp(`^${item}$`, "i").test(part)) ? (
            <span style={{ background: "yellow" }} key={i}>
              {part}
            </span>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
    </span>
  );
};

export default function CompanyList(props: {
  hideFlags?: boolean;
  filters: {
    keyword: string;
    tags: number[];
    flagged?: boolean;
    inSearch?: boolean;
    searchMode?: "classic" | "better";
    setKeyword?: Function;
  };
  sortedCompanies?: Company[];
  onPageChange?: (newPage: number) => void;
}) {
  const { hideFlags = false, filters, sortedCompanies, onPageChange } = props;
  const {
    keyword = "",
    tags = [],
    flagged = false,
    inSearch = false,
    searchMode = "better",
    setKeyword = () => {},
  } = filters;

  const [searchParams, setSearchParams] = useSearchParams();
  const { pagination, map } = useSelector((state: RootState) => state.company);
  const { page, pageCount, total } = pagination;
  const dispatch = useDispatch<any>();

  // The server already filtered, ranked and paginated these. Do NOT re-filter.
  // NOTE: `map` must contain ONLY the current page's companies (see reducer note).
  const visibleCompanies: Company[] = sortedCompanies || Array.from(map.values());

  useEffect(() => {
    const page = searchParams.get("page") ?? "1";
    const keywordParam = searchParams.get("keyword") ?? "";
    if (!page && !keywordParam) {
      const urlParam = new URLSearchParams();
      urlParam.append("page", "1");
      urlParam.append("keyword", "");
      setSearchParams(urlParam);
    } else if (keywordParam.trim()) {
      setKeyword(keywordParam);
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const handler = setTimeout(() => {
      dispatch(
        getCompanyPage({
          pageNumber: 1,
          keyword,
          tags,
          flagged,
          inSearch,
          searchMode,
        })
      );
      const urlParams = searchParams;
      urlParams.set("page", "1");
      urlParams.set("keyword", keyword);
      setSearchParams(urlParams);
    }, 250);
    return () => clearTimeout(handler);
  }, [keyword, tags, searchMode, flagged, inSearch]);

  const handlePageChange = (event: any) => {
    const newPage = event.selected + 1;
    if (props.onPageChange) {
      props.onPageChange(newPage);
    }

    if (!onPageChange) {
      dispatch(
        getCompanyPage({
          pageNumber: newPage,
          keyword,
          tags,
          flagged,
          inSearch,
          searchMode,
        })
      ).then(() => window.scrollTo({ top: 0, behavior: "smooth" }));
      const urlParam = searchParams;
      urlParam.set("page", newPage);
      setSearchParams(urlParam);
    }
  };

  // Split on spaces and '+', collapse multiple spaces (used for highlighting only)
  const keywords = (keyword || "")
    .replace(/\+/g, " ")
    .split(/\s+/)
    .map((k) => k.trim().toLowerCase())
    .filter(Boolean);

  const hasValidKeywords = keywords.length > 0;
  const tagSet = new Set(tags);
  const phrase = (keyword || "").trim().replace(/\s+/g, " ");

  // Ranks a detail line so the most relevant nuggets/sources show first.
  const detailRank = (text?: string) => {
    const value = (text || "").toLowerCase();
    const normalizedPhrase = phrase.toLowerCase();
    if (!value) return 0;
    if (
      normalizedPhrase &&
      (value.includes(normalizedPhrase) ||
        value.replace(/[\s\-_]+/g, "").includes(normalizedPhrase.replace(/[\s\-_]+/g, "")))
    ) {
      return 10000;
    }
    // Earlier search words outrank later ones. A whole word outranks a partial one.
    for (let i = 0; i < keywords.length; i++) {
      if (!value.includes(keywords[i])) continue;
      const wholeWord = new RegExp(`\\b${escapeRegExp(keywords[i])}\\b`, "i");
      const wordPriority = (keywords.length - i) * 10;
      return wordPriority + (wholeWord.test(value) ? 5 : 0);
    }
    return 0;
  };

  const matchingDetails = <T,>(items: T[] | undefined, textOf: (item: T) => string) =>
    (items || [])
      .map((item) => ({ item, rank: detailRank(textOf(item)) }))
      .filter((entry) => entry.rank > 0)
      .sort((a, b) => b.rank - a.rank)
      .map((entry) => entry.item);

  return (
    <div className="container-fluid px-0">
      <div className="companies-table">
        <div className="table-responsive bg-white">
          <table className="table global-table table-stripped">
            <thead>
              <tr>
                <th>ID</th>
                <th className="text-left px-5">Company Name</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visibleCompanies.map((company: Company) => {
                const { companyName, flagColor, id } = company;

                let nuggets: Nugget[] | undefined;
                let tagsText: Tag[] | undefined;
                let sources: companySource[] | undefined;
                let partners: Company[] | undefined;
                let flags: Flag[] | undefined;

                if (hasValidKeywords) {
                  nuggets = matchingDetails(company.nuggets, (nugget) => nugget.nugget);
                  tagsText = matchingDetails(company.companyTags, (tag) => tag.tagName);
                  sources = matchingDetails(
                    company.companySources,
                    (source) => source.source
                  );
                  partners = matchingDetails(
                    company.partners,
                    (partner) => partner.companyName ?? partner.partners ?? ""
                  );
                  flags = matchingDetails(company.companyFlags, (flag) => flag.flag);
                }

                const tagsFilter: Tag[] | undefined =
                  tags.length > 0
                    ? company.companyTags?.filter((tag) => tagSet.has(tag.id))
                    : undefined;

                return (
                  <tr key={id}>
                    <th>{id}</th>
                    <td className="pl-5">
                      <Link
                        to={`view/${id}`}
                        className={styles.name}
                        draggable
                        onDragStart={(e) =>
                          e.dataTransfer.setData("text/plain", companyName)
                        }
                      >
                        {companyName}
                      </Link>
                      {flagColor !== "none" && hideFlags === false && (
                        <i
                          className={`fa fa-flag ${
                            flagColor === "orange"
                              ? "text-warning"
                              : flagColor === "green"
                              ? "text-success"
                              : flagColor === "black"
                              ? "text-dark"
                              : ""
                          } mx-2`}
                        ></i>
                      )}
                      <span> N : {company.nuggets?.length ?? 0}</span>
                      <span> S : {company.companySources?.length ?? 0}</span>
                      <span> P : {company.partners?.length ?? 0}</span>

                      <div className={styles.details}>
                        {nuggets && nuggets.length > 0 && (
                          <div>
                            <h6>Nuggets</h6>
                            <ul>
                              {nuggets.map((nugget) => (
                                <li key={nugget.id}>
                                  <Highlighted
                                    text={nugget.nugget}
                                    highlights={keywords}
                                    phrase={phrase}
                                  />
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {tagsText && tagsText.length > 0 && (
                          <div>
                            <h6>Tags</h6>
                            <ul>
                              {tagsText.map((tag) => (
                                <li key={tag.id}>
                                  <Highlighted
                                    text={tag.tagName}
                                    highlights={keywords}
                                    phrase={phrase}
                                  />
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {sources && sources.length > 0 && (
                          <div>
                            <h6>Sources</h6>
                            <ul>
                              {sources.map((source) => (
                                <li key={source.id}>
                                  <Highlighted
                                    text={source.source}
                                    highlights={keywords}
                                    phrase={phrase}
                                  />
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {partners && partners.length > 0 && (
                          <div>
                            <h6>Partners</h6>
                            <ul>
                              {partners.map((partner, i) => (
                                <li key={partner?.id ?? i}>
                                  <Highlighted
                                    text={partner?.companyName ?? partner?.partners}
                                    highlights={keywords}
                                    phrase={phrase}
                                  />
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {flags && flags.length > 0 && (
                          <div>
                            <h6>Flags</h6>
                            <ul>
                              {flags.map((flag) => (
                                <li key={flag.id}>
                                  <Highlighted
                                    text={flag.flag}
                                    highlights={keywords}
                                    phrase={phrase}
                                  />
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {tagsFilter && tagsFilter.length > 0 && (
                          <div>
                            <h6>Selected Tags</h6>
                            <ul>
                              {tagsFilter.map((tag) => (
                                <li key={tag.id}>
                                  <Highlighted
                                    text={tag.tagName}
                                    highlights={keywords}
                                    phrase={phrase}
                                  />
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="text-right px-5"></td>
                  </tr>
                );
              })}
              {visibleCompanies.length === 0 && (
                <EmptyList title="No results found !" subtitle="" />
              )}
              <tr className={styles.__page_fix__}>
                <td colSpan={3}>
                  <div>
                    <Paginate
                      page={page}
                      pageCount={pageCount}
                      handlePageChange={handlePageChange}
                      total={total}
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="footer">
        <Link to="/terms">Terms &amp; Condition Privacy Policy</Link>
      </div>
    </div>
  );
}